// HSSNA TanStack Query — 集中化 QueryClient 工厂 + 单例
// 规格: ADR-0010 / SPEC §4.3 / NF-PERF-03 / NF-PERF-04
// 关联: P1-4 (本文件) / P1-5 (query-keys.ts) / P1-6 (http/http-error.ts)
//
// 设计:
//   - createQueryClient(opts?): 工厂函数,各 app 入口调用 (支持 SSR / 测试隔离);
//     opts 可覆盖默认 staleTime / refetchOnWindowFocus, 以兼容个别 app 的差异需求
//     (如 elder-screen 适老化大屏需 60s staleTime)
//   - queryClient: 预配置单例 (默认 30s staleTime), 供 SSR / 测试 / 非 React 上下文使用,
//     也可供配置与默认一致的 app 直接复用, 避免重复 new QueryClient({...})
//   - 默认 staleTime 30s (P1-5 按域分级 STALE_TIMES 可覆盖)
//   - retry: 401/403/404 不重试 (见 P1-6 HttpError.retryable),其他最多 1 次
//   - refetchOnWindowFocus: false (各 app 按需开启,如 medical-cws 切窗回焦点)
//   - refetchOnReconnect: true (ADR-0006 边缘离线恢复后自动重拉)
//   - gcTime: 5min (垃圾回收)

import { QueryClient } from '@tanstack/query-core';
import { isHttpError } from './http/http-error.js';

/**
 * createQueryClient 配置项 (所有字段可选, 缺省使用 P1-4 默认值)
 *
 * 设计意图: 各 app 入口配置与默认有差异时 (如 elder-screen staleTime=60s),
 * 通过 opts 显式声明差异, 其余配置仍走集中化默认, 避免散落 new QueryClient({...})
 */
export interface CreateQueryClientOptions {
    /** 默认 staleTime (毫秒), 默认 30_000 (30s) */
    readonly defaultStaleTime?: number;
    /** 切窗回焦点是否自动 refetch, 默认 false (各 app 按需开启) */
    readonly refetchOnWindowFocus?: boolean;
}

/**
 * 创建预配置的 QueryClient
 *
 * 各 app 在入口调用此工厂创建独立实例,避免跨 app 缓存污染;
 * 配置与默认一致时可直接复用 `queryClient` 单例.
 *
 * @example
 * ```ts
 * // apps/xxx/main.tsx — 配置与默认一致, 直接用单例
 * import { queryClient } from '@hssna/ts-shared';
 * <QueryClientProvider client={queryClient}>...</QueryClientProvider>
 *
 * // apps/elder-screen/main.tsx — 适老化大屏需 60s staleTime
 * import { createQueryClient } from '@hssna/ts-shared';
 * const queryClient = createQueryClient({ defaultStaleTime: 60_000 });
 * ```
 */
export function createQueryClient(options: CreateQueryClientOptions = {}): QueryClient {
    const {
        defaultStaleTime = 30_000,
        refetchOnWindowFocus = false,
    } = options;
    return new QueryClient({
        defaultOptions: {
            queries: {
                // 全局重试 1 次;401/403/404 不重试 (见 HttpError.retryable)
                retry: (failureCount, error) => {
                    if (isHttpError(error)) {
                        return error.retryable && failureCount < 1;
                    }
                    return failureCount < 1;
                },
                // 默认 30s;医疗关键域由 STALE_TIMES 覆盖为 0 (见 query-keys.ts);
                // app 可通过 opts.defaultStaleTime 覆盖 (如 elder-screen 60s)
                staleTime: defaultStaleTime,
                // 切窗回焦点不自动 refetch (各 app 按需开启 / opts 覆盖)
                refetchOnWindowFocus,
                // 网络恢复后自动 refetch (ADR-0006 边缘离线恢复)
                refetchOnReconnect: true,
                // 5 分钟垃圾回收
                gcTime: 5 * 60 * 1000,
            },
            mutations: {
                // 写操作不自动重试 (Idempotency-Key 已消费,见 ADR-0010)
                retry: false,
            },
        },
    });
}

/**
 * 预配置 QueryClient 单例 (默认 staleTime=30s)
 *
 * 用途:
 *   - SSR / 测试 / 非 React 上下文 (如 service worker / background script)
 *   - 配置与默认一致的 app 入口直接复用, 替代重复 `new QueryClient({...})`
 *
 * 注: 有差异需求的 app (如 elder-screen 60s staleTime) 请改用 createQueryClient(opts)
 */
export const queryClient: QueryClient = createQueryClient();
